
/**
 * Event utilities to manage custom events in a more predictable way
 * with debounce functionality to prevent excessive event handling
 */

// Define event names as constants to avoid typos
export const EVENT_NAMES = {
  ADMIN_LOGIN_SUCCESS: 'admin-login-success',
  ADMIN_LOGOUT: 'admin-logout',
  PORTFOLIO_UPDATED: 'portfolio-updated'
};

// Track last event times to prevent duplicate events
const lastEventTimes: Record<string, number> = {};
const EVENT_COOLDOWN = 2000; // 2 seconds cooldown to prevent rapid firing

// Track which listeners are active to prevent duplicate listeners
const activeListeners: Record<string, Set<Function>> = {};

/**
 * Debounce helper to prevent multiple events firing too quickly
 */
export const debounce = <T extends (...args: any[]) => any>(fn: T, ms = 500) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(null, args), ms);
  };
};

/**
 * Safely dispatch a custom event with throttling to prevent freezes
 */
export function dispatchCustomEvent(eventName: string, data: any = {}) {
  // Prevent duplicate events within cooldown period
  const now = Date.now();
  const lastTime = lastEventTimes[eventName] || 0;
  
  if (now - lastTime < EVENT_COOLDOWN) {
    console.log(`Event ${eventName} throttled (too frequent)`);
    return false;
  }
  
  lastEventTimes[eventName] = now;
  
  try {
    console.log(`Dispatching event: ${eventName}`, data);
    const event = new CustomEvent(eventName, { detail: data });
    window.dispatchEvent(event);
    return true;
  } catch (error) {
    console.error(`Error dispatching event ${eventName}:`, error);
    return false;
  }
}

// Debounced version for events that might fire rapidly, increased delay
export const dispatchDebouncedEvent = debounce(dispatchCustomEvent, 800);

/**
 * Add an event listener with proper type safety and duplicate prevention
 */
export function addCustomEventListener(
  eventName: string, 
  handler: (data?: any) => void,
  options?: boolean | AddEventListenerOptions
) {
  // Initialize set if it doesn't exist
  if (!activeListeners[eventName]) {
    activeListeners[eventName] = new Set();
  }
  
  // Check if this exact handler function is already registered
  const handlerAlreadyRegistered = Array.from(activeListeners[eventName]).some(
    existingHandler => existingHandler.toString() === handler.toString()
  );
  
  if (handlerAlreadyRegistered) {
    console.log(`Event listener for ${eventName} already registered, skipping duplicate`);
    // Return a no-op cleanup function since we didn't add a new listener
    return () => {};
  }
  
  // Create a wrapper to manage reference tracking
  const wrappedHandler = (event: Event) => {
    try {
      const customEvent = event as CustomEvent;
      handler(customEvent.detail);
    } catch (error) {
      console.error(`Error in event handler for ${eventName}:`, error);
    }
  };
  
  // Store reference to allow duplicate detection
  activeListeners[eventName].add(handler);
  
  // Add the actual event listener
  window.addEventListener(eventName, wrappedHandler, options);
  
  // Return cleanup function that also removes from our tracking set
  return () => {
    window.removeEventListener(eventName, wrappedHandler);
    activeListeners[eventName].delete(handler);
  };
}

/**
 * Create a one-time event listener with safety mechanisms
 */
export function addOneTimeEventListener(
  eventName: string,
  handler: (data?: any) => void,
  timeout?: number
) {
  let hasExecuted = false;
  let timeoutId: number | undefined;
  
  const wrappedHandler = (event: Event) => {
    if (hasExecuted) return;
    
    hasExecuted = true;
    if (timeoutId) window.clearTimeout(timeoutId);
    
    try {
      const customEvent = event as CustomEvent;
      handler(customEvent.detail);
    } catch (error) {
      console.error(`Error in one-time event handler for ${eventName}:`, error);
    }
    
    window.removeEventListener(eventName, wrappedHandler);
    
    // Also remove from our tracking if it exists there
    if (activeListeners[eventName]) {
      activeListeners[eventName].delete(handler);
    }
  };
  
  window.addEventListener(eventName, wrappedHandler);
  
  if (timeout) {
    timeoutId = window.setTimeout(() => {
      if (!hasExecuted) {
        window.removeEventListener(eventName, wrappedHandler);
        if (activeListeners[eventName]) {
          activeListeners[eventName].delete(handler);
        }
      }
    }, timeout);
  }
  
  // Return cleanup function
  return () => {
    if (timeoutId) window.clearTimeout(timeoutId);
    window.removeEventListener(eventName, wrappedHandler);
    if (activeListeners[eventName]) {
      activeListeners[eventName].delete(handler);
    }
  };
}
